import { useEffect, useState, useCallback } from "react";
import {
  useToast,
  Box,
  Heading,
  Button,
  Flex,
  HStack,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Grid,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from "@chakra-ui/react";
import { WarningIcon, RefreshIcon, SearchIcon, FilterIcon, DownloadIcon, ChevronDownIcon } from "../../utils/icons";
import { useInfractions } from "../../hooks/useInfractions";
import ImageModal from "./ImageModal";
import FilterDrawer from "./FilterDrawer";
import PlateInfractionCard from "./PlateInfractionCard";
import PeriodStatsTab from "./PeriodStatsTab";
import UserStatsTab from "./UserStatsTab";
import AnalysisTab from "./AnalysisTab";
import ValidationTab from "./ValidationTab";
import WhitelistTab from "../WhitelistTab";
import RegisteredPlatesTab from "./RegisteredPlatesTab";

const InfractionsMain = () => {
  const [cameras, setCameras] = useState([]);
  const [algorithms, setAlgorithms] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedInfraction, setSelectedInfraction] = useState(null);
  const [userLevel, setUserLevel] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    cameraId: "",
    algorithmId: "",
    startDate: "",
    endDate: "",
    plate: "",
    infractionClass: "",
    plateContains: false, // Adicionar o campo plateContains
    startTime: "", // novo: horário inicial (HH:MM)
    endTime: ""    // novo: horário final (HH:MM)
  });
  const [currentFilters, setCurrentFilters] = useState(null); // Para armazenar os filtros ativos
  const [currentInfractionIndex, setCurrentInfractionIndex] = useState(-1);
  const [isExporting, setIsExporting] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast(); const {
    infractions,
    filteredInfractions,
    loading,
    loadingMore,
    pagination,
    searchStats,
    fetchInfractions,
    fetchLast24HoursInfractions,
    searchInfractions,
    updateInfractionStatus,
    searchInfractionsLoadMore,
    fetchLast24HoursLoadMore,
    exportSearchData,
    exportToPDF
  } = useInfractions();

  useEffect(() => {
    const initializeData = async () => {
      try {
        const level = await fetchUserLevel();
        await Promise.all([
          fetchCameras(),
          fetchAlgorithms()
        ]);

        await fetchInfractions(false, level);
      } catch (error) {
        console.error('Erro ao inicializar dados:', error);
        toast({
          title: "Erro ao carregar dados",
          description: "Erro ao inicializar a tela",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, []);

  const fetchUserLevel = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const response = await fetch('/api/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return 1;
        }
        throw new Error('Erro ao buscar informações do usuário');
      }

      const data = await response.json();
      console.log('Nível do usuário obtido:', data.role);
      setUserLevel(data.role);
      return data.role;
    } catch (error) {
      console.error('Erro ao buscar nível do usuário:', error);
      setUserLevel(1);
      return 1;
    }
  };

  const fetchCameras = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const response = await fetch('/api/cameras', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        throw new Error('Erro ao buscar câmeras');
      }

      const data = await response.json();
      setCameras(data);
    } catch (error) {
      console.error('Erro ao buscar câmeras:', error);
      toast({
        title: "Erro ao carregar câmeras",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchAlgorithms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/algorithms', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar algoritmos');

      const data = await response.json();
      setAlgorithms(data);
    } catch (error) {
      console.error('Erro ao buscar algoritmos:', error);
    }
  };
  const refreshInfractions = async () => {
    setCurrentFilters(null); // Limpar filtros ao atualizar
    await fetchInfractions(false, userLevel);
    toast({
      title: "Infrações atualizadas",
      description: "Carregadas as infrações mais recentes",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };
  // Nova função para buscar últimas 24h
  const handleLast24Hours = async () => {
    setCurrentFilters(null); // Limpar filtros ao buscar últimas 24h
    await fetchLast24HoursInfractions(userLevel <= 2);
    toast({
      title: "Infrações das últimas 24h",
      description: userLevel <= 2 ? "Carregadas suas análises das últimas 24h" : "Carregadas infrações das últimas 24h",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getCameraName = (cameraId) => {
    if (!cameraId) return 'Câmera desconhecida';
    const cameraIdNum = typeof cameraId === 'string' ? parseInt(cameraId, 10) : cameraId;
    const camera = cameras.find(cam => cam.id === cameraIdNum);
    return camera ? camera.name : `Câmera ID: ${cameraId}`;
  };

  const getAlgorithmName = (algorithmId) => {
    if (!algorithmId) return 'Algoritmo desconhecido';
    const algorithmIdNum = typeof algorithmId === 'string' ? parseInt(algorithmId, 10) : algorithmId;
    const algorithm = algorithms.find(alg => alg.id === algorithmIdNum);
    return algorithm ? algorithm.name : `Algoritmo ID: ${algorithmId}`;
  };

  const openImageModal = (imageUrl, infraction) => {
    setSelectedImage(imageUrl);
    setSelectedInfraction(infraction);

    // CORREÇÃO: Encontrar o índice da infração selecionada
    const infractionIndex = filteredInfractions.findIndex(inf => inf.id === infraction.id);
    setCurrentInfractionIndex(infractionIndex >= 0 ? infractionIndex : 0);

    setIsModalOpen(true);
  }; const applyAdvancedFilters = async () => {
    console.log('Aplicando filtros:', filters);

    // Verificar se pelo menos um filtro foi preenchido - corrigir a verificação
    const hasFilters = Object.entries(filters).some(([key, value]) => {
      // Para campos boolean (como plateContains), não verificar como string
      if (typeof value === 'boolean') return false;
      // Para outros campos, verificar se é string e não está vazio
      return value && typeof value === 'string' && value.trim() !== '';
    });

    if (!hasFilters) {
      toast({
        title: "Nenhum filtro aplicado",
        description: "Preencha pelo menos um campo para filtrar",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Criar uma cópia dos filtros para armazenar
    const filtersToApply = { ...filters };

    // Mesclar data + hora se fornecidas
    if (filtersToApply.startDate) {
      const time = filtersToApply.startTime && filtersToApply.startTime.trim() !== '' ? filtersToApply.startTime : '00:00:00';
      filtersToApply.startDate = `${filtersToApply.startDate} ${time}`;
    }
    if (filtersToApply.endDate) {
      const time = filtersToApply.endTime && filtersToApply.endTime.trim() !== '' ? filtersToApply.endTime : '23:59:59.999';
      // backend pode esperar fim do dia; ajuste conforme necessário
      filtersToApply.endDate = `${filtersToApply.endDate} ${time}`;
    }

    // Remove campos temporários se necessário (se backend não suporta startTime/endTime)
    delete filtersToApply.startTime;
    delete filtersToApply.endTime;

    // Chamar a função que faz a busca com filtersToApply
    await searchInfractions(filtersToApply);
    setCurrentFilters(filtersToApply); // Armazenar os filtros aplicados
    onClose();

    // O toast será atualizado pelo useEffect quando as estatísticas forem calculadas
    toast({
      title: "Filtros aplicados",
      description: "Busca realizada com sucesso",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const resetFilters = async () => {
    const emptyFilters = {
      status: "",
      cameraId: "",
      algorithmId: "",
      startDate: "",
      endDate: "",
      plate: "",
      infractionClass: "",
      plateContains: false, // Incluir no reset
      startTime: "",
      endTime: ""
    };

    setFilters(emptyFilters);
    setCurrentFilters(null); // Limpar filtros armazenados

    // Voltar à busca normal de infrações
    await fetchInfractions(false, userLevel);

    toast({
      title: "Filtros limpos",
      description: "Exibindo todas as infrações",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  // Função para exportar dados da busca
  const handleExportData = async (format = 'pdf') => {
    if (!currentFilters) {
      toast({
        title: "Nenhum filtro aplicado",
        description: "Aplique filtros antes de exportar",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsExporting(true);
    try {
      if (format === 'pdf') {
        await exportToPDF(currentFilters, filteredInfractions, searchStats);
      } else {
        await exportSearchData(currentFilters);
      }
    } catch (error) {
      console.error('Erro na exportação:', error);
    } finally {
      setIsExporting(false);
    }
  };
  const loadMoreInfractions = () => {
    // Verificar qual tipo de filtro está ativo baseado no estado da paginação
    if (pagination.activeFilter === 'search' && currentFilters) {
      // Se há filtros de busca ativos, continuar com a busca filtrada
      searchInfractionsLoadMore(currentFilters);
    } else if (pagination.activeFilter === 'last24h') {
      // Se está mostrando últimas 24h, continuar com essa busca
      fetchLast24HoursLoadMore(userLevel <= 2);
    } else {
      // Busca normal
      fetchInfractions(true, userLevel);
    }
  };

  // Função para carregar mais infrações durante navegação no modal
  const handleLoadMoreInModal = useCallback(async () => {
    if (!currentFilters) {
      // Busca normal - passar userLevel
      await loadMoreInfractions(userLevel);
    } else {
      // Busca com filtros
      await searchInfractionsLoadMore(currentFilters);
    }
  }, [currentFilters, loadMoreInfractions, searchInfractionsLoadMore, userLevel]);

  // Estado para controlar aba ativa e se a página foi rolada
  const [activeTab, setActiveTab] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let rafId = null;
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 80);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // verificar estado inicial (caso a página já esteja rolada)
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Ajustar aba ativa quando o userLevel mudar
  useEffect(() => {
    // Se o usuário tem nível baixo (<2), começar na aba "Lista de Infrações"
    if (userLevel < 2) {
      setActiveTab(0); // Lista de Infrações será o índice 0
    }
  }, [userLevel]);

  // Calcular o índice da aba "Lista de Infrações" baseado no nível do usuário
  let listTabIndex;
  if (userLevel >= 3) {
    // 0: Temporais, 1: Usuários, 2: Análise, 3: Validação, 4: Lista
    listTabIndex = 4;
  } else if (userLevel === 2) {
    // 0: Temporais, 1: Validação, 2: Lista
    listTabIndex = 2;
  } else {
    // 0: Lista
    listTabIndex = 0;
  }

  return (
    <Box p={6} flex="1" minH="100vh" bg="gray.100">
     <Button
       position="fixed"
       top="16px"
       right="16px"
       zIndex="overlay"
       colorScheme="teal"
       onClick={onOpen}
       leftIcon={<FilterIcon />}
       borderRadius="md"
       boxShadow="md"
       transition="opacity 200ms ease, transform 200ms ease"
      opacity={(isScrolled && activeTab === listTabIndex) ? 1 : 0}
      transform={(isScrolled && activeTab === listTabIndex) ? 'translateY(0)' : 'translateY(-8px)'}
      pointerEvents={(isScrolled && activeTab === listTabIndex) ? 'auto' : 'none'}
     >
       Filtros
     </Button>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading display="flex" alignItems="center">
          <WarningIcon />
          Infrações Detectadas
        </Heading>
        {/* Botões visíveis apenas quando a aba "Lista de Infrações" estiver ativa */}
        <HStack
          transition="opacity 200ms ease, transform 200ms ease"
          opacity={activeTab === listTabIndex ? 1 : 0}
          transform={activeTab === listTabIndex ? 'translateY(0)' : 'translateY(-6px)'}
          pointerEvents={activeTab === listTabIndex ? 'auto' : 'none'}
        >
          <Button
            colorScheme="green"
            onClick={refreshInfractions}
            leftIcon={<RefreshIcon />}
            isLoading={loading}
            loadingText="Atualizando"
          >
            Atualizar
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<SearchIcon />}
            onClick={handleLast24Hours}
          >
            Últimas 24h
          </Button>
          <Button colorScheme="teal" onClick={onOpen} leftIcon={<FilterIcon />}>
            Filtros Avançados
          </Button>
        </HStack>
      </Flex>

      {/* Tabs controladas para manter activeTab sincronizado (necessário para controlar visibilidade do botão) */}
      <Tabs variant="soft-rounded" colorScheme="red" mb={4} index={activeTab} onChange={(index) => setActiveTab(index)} isLazy lazyBehavior="unmount">
        <TabList>
          {userLevel >= 2 && <Tab>Estatísticas Temporais</Tab>}
          {userLevel > 2 && <Tab>Estatísticas de Usuários</Tab>}
          {userLevel >= 3 && <Tab>Análise de Usuários</Tab>}
          {userLevel >= 2 && <Tab>Validação</Tab>}
          <Tab>Lista de Infrações</Tab>
          {userLevel >= 2 && <Tab>Placas Cadastradas</Tab>}
          {userLevel >= 2 && <Tab>Whitelist</Tab>}
        </TabList>

        <TabPanels>
          {userLevel >= 2 && (
            <TabPanel>
              <PeriodStatsTab userLevel={userLevel} />
            </TabPanel>
          )}

          {userLevel > 2 && (
            <TabPanel>
              <UserStatsTab userLevel={userLevel} />
            </TabPanel>
          )}

          {userLevel >= 3 && (
            <TabPanel>
              <AnalysisTab />
            </TabPanel>
          )}

          {userLevel >= 2 && (
            <TabPanel>
              <ValidationTab userLevel={userLevel} />
            </TabPanel>
          )}

          <TabPanel>
            {/* Indicador de filtros ativos e estatísticas */}
            {currentFilters && (
              <Box mb={4} p={4} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" color="blue.700" fontWeight="medium">
                    Filtros ativos: {Object.entries(currentFilters)
                      .filter(([key, value]) => {
                        // Para campos boolean, verificar se é true
                        if (typeof value === 'boolean') return value;
                        // Para outros campos, verificar se é string e não está vazio
                        return value && typeof value === 'string' && value.trim() !== '';
                      })
                      .map(([key, value]) => {
                        if (key === 'cameraId') return `Câmera: ${getCameraName(value)}`;
                        if (key === 'algorithmId') return `Algoritmo: ${getAlgorithmName(value)}`;
                        if (key === 'status') return `Status: ${value}`;
                        if (key === 'plate') return `Placa: ${value}`;
                        if (key === 'plateContains') return `Busca por conteúdo: ${value ? 'Sim' : 'Não'}`;
                        if (key === 'startDate') return `De: ${new Date(value).toLocaleDateString('pt-BR')}`;
                        if (key === 'endDate') return `Até: ${new Date(value).toLocaleDateString('pt-BR')}`;
                        return `${key}: ${value}`;
                      })
                      .join(', ')
                    }
                  </Text>
                  <HStack>
                    <Menu>
                      <MenuButton 
                        as={Button}
                        size="sm" 
                        colorScheme="green" 
                        leftIcon={<DownloadIcon />}
                        rightIcon={<ChevronDownIcon />}
                        isLoading={isExporting}
                        loadingText="Exportando..."
                      >
                        Baixar dados da busca
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => handleExportData('pdf')}>
                          <DownloadIcon mr={2} />
                          Relatório PDF (com imagens)
                        </MenuItem>
                        <MenuItem onClick={() => handleExportData('zip')}>
                          <DownloadIcon mr={2} />
                          Arquivo ZIP (dados + imagens)
                        </MenuItem>
                      </MenuList>
                    </Menu>
                    <Button size="sm" onClick={resetFilters} colorScheme="blue" variant="outline">
                      Limpar Filtros
                    </Button>
                  </HStack>
                </Flex>
                
                {/* Exibir estatísticas da busca */}
                {searchStats && (
                  <Grid templateColumns="repeat(auto-fit, minmax(120px, 1fr))" gap={3} mt={3}>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">{searchStats.total}</Text>
                      <Text fontSize="xs" color="gray.600">Total</Text>
                    </Box>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="green.600">{searchStats.confirmed}</Text>
                      <Text fontSize="xs" color="gray.600">Confirmadas</Text>
                    </Box>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="red.600">{searchStats.rejected}</Text>
                      <Text fontSize="xs" color="gray.600">Rejeitadas</Text>
                    </Box>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="orange.600">{searchStats.pending}</Text>
                      <Text fontSize="xs" color="gray.600">Pendentes</Text>
                    </Box>
                  </Grid>
                )}
              </Box>
            )}

            {initialLoading || loading ? (
              <Flex justify="center" my={12}>
                <Spinner size="xl" />
              </Flex>
            ) : filteredInfractions.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={12} bg="white" borderRadius="md" boxShadow="sm">
                <Text fontSize="xl" mb={4}>
                  {userLevel <= 2 
                    ? "Você ainda não analisou nenhuma infração" 
                    : "Nenhuma infração encontrada"
                  }
                </Text>
                {userLevel <= 2 ? (
                  <Text fontSize="md" color="gray.600" textAlign="center" mb={4}>
                    Infrações aparecerão aqui após você analisá-las em lotes atribuídos a você.
                  </Text>
                ) : (
                  <Button colorScheme="teal" onClick={resetFilters}>Limpar Filtros</Button>
                )}
              </Flex>
            ) : (
              <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
                {filteredInfractions.map((infraction) => (
                  <PlateInfractionCard
                    key={infraction.id}
                    infraction={infraction}
                    openImageModal={openImageModal}
                    getAlgorithmName={getAlgorithmName}
                    getCameraName={getCameraName}
                  />
                ))}
              </Grid>
            )}

            {pagination.hasMore && (
              <Flex justify="center" mt={4} mb={6}>
                <Button
                  onClick={loadMoreInfractions}
                  isLoading={loadingMore}
                  loadingText="Carregando..."
                  colorScheme="blue"
                  size="lg"
                  width="200px"
                >
                  Ver Mais
                </Button>
              </Flex>
            )}
          </TabPanel>

          {userLevel >= 2 && (
            <TabPanel>
              <RegisteredPlatesTab />
            </TabPanel>
          )}

          {userLevel >= 2 && (
            <TabPanel>
              <WhitelistTab />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* ImageModal */}
      {selectedInfraction && selectedImage && (
        <ImageModal
          isOpen={!!selectedInfraction}
          onClose={() => {
            setSelectedInfraction(null);
            setSelectedImage(null);
            setCurrentInfractionIndex(-1);
          }}
          selectedImage={selectedImage}
          selectedInfraction={selectedInfraction}
          updateInfractionStatus={updateInfractionStatus}
          getAlgorithmName={getAlgorithmName}
          getCameraName={getCameraName}
          infractions={filteredInfractions}
          currentIndex={currentInfractionIndex}
          setCurrentIndex={setCurrentInfractionIndex}
          loadMoreInfractions={handleLoadMoreInModal}
          hasMore={pagination.hasMore}
          isLoadingMore={loadingMore}
          userLevel={userLevel}
        />
      )}

      <FilterDrawer
        isOpen={isOpen}
        onClose={onClose}
        filters={filters}
        handleFilterChange={handleFilterChange}
        cameras={cameras}
        algorithms={algorithms}
        applyAdvancedFilters={applyAdvancedFilters}
        resetFilters={resetFilters}
      />
    </Box>
  );
};

export default InfractionsMain;